import React, { createRef, useState } from 'react';

import {
  ADJOINED,
  Input as BaseInput,
  StyledInputContainer as BaseInputContainer,
  StyledInputEnhancer as BaseInputEnhancer,
  StyledInput as BaseStyledInput,
  StyledRoot as BaseStyledInputRoot,
  StatefulContainer,
} from 'baseui/input';
import { StyledMaskToggleButton as BaseStyledMaskToggleButton } from 'baseui/input/styled-components';
import PropTypes from 'prop-types';

import { styled, useStyletron, withStyle } from 'shared/components/ihcl/styled';

import { cssValueMath } from '.';
import { HideIcon, ViewIcon } from './icon';

export const getInputFillColor = ({
  $error,
  $isFocused,
  $positive,
  $theme,
}) => {
  let inputFillColor = $isFocused
    ? $theme.colors.inputFillActive
    : $theme.colors.inputFill;
  if ($positive && !$isFocused) {
    inputFillColor = $theme.colors.inputFillPositive;
  }
  if ($error && !$isFocused) {
    inputFillColor = $theme.colors.inputFillError;
  }
  return inputFillColor;
};

export const getLabelColor = ({
  $disabled,
  $error,
  $isFocused,
  $positive,
  $theme,
}) => {
  if (!$theme || !$theme.colors) {
    console.error('$theme not found', { $theme });
    return 'black';
  }
  let color = $isFocused ? $theme.colors.primary60 : $theme.colors.grayLabel;
  if ($positive && !$isFocused) {
    color = $theme.colors.positive70;
  }
  if ($error && !$isFocused) {
    color = $theme.colors.negative70;
  }
  if ($disabled) {
    color = $theme.colors.disabled;
  }
  return color;
};

export const StyledInputEnhancer = withStyle(BaseInputEnhancer, ({ $size }) => {
  const styles = {
    backgroundColor: 'transparent',
  };
  if ($size === 'tiny') {
    styles.paddingLeft = 0;
  }
  return styles;
});

export const StyledMaskToggleButton = withStyle(
  BaseStyledMaskToggleButton,
  ({ $error = false, $isFocused = false, $positive = false, $theme }) => ({
    color: getLabelColor({ $error, $isFocused, $positive, $theme }),
  })
);

export const StyledInputContainer = withStyle(BaseInputContainer, () => ({
  backgroundColor: 'transparent',
  position: 'relative',
}));

export const Label = styled(
  'label',
  ({
    $disabled,
    $error,
    $isFocused,
    $positive,
    $size,
    $theme,
    $top = 0,
    $value,
  }) => ({
    color: getLabelColor({ $disabled, $error, $isFocused, $positive, $theme }),
    display: ['compact', 'tiny'].includes($size) && $value ? 'none' : 'block',
    backgroundColor: 'transparent',
    position: 'absolute',
    left:
      $size === 'compact'
        ? $theme.sizing.inputCompactPadding
        : $theme.sizing.inputPaddingLeft,
    top: $top,
    fontFamily: $theme.typography.font100.fontFamily,
    fontSize: ['compact', 'tiny'].includes($size)
      ? $theme.sizing.unit12
      : '15px',
    lineHeight: ['compact', 'tiny'].includes($size)
      ? $theme.sizing.unit12
      : '15px',
    paddingTop: '6px',
    fontWeight: 500,
    transition: '200ms all linear',
  })
);

export const Caption = styled(
  'div',
  ({
    $error = false,
    $isFocused = false,
    $positive = false,
    $collapseCaptionPadding = false,
    $collapseCaptionHorizontalPadding = false,
    $theme,
  }) => ({
    color: getLabelColor({ $error, $isFocused, $positive, $theme }),
    paddingLeft: $collapseCaptionHorizontalPadding
      ? 0
      : cssValueMath(
          $theme.sizing.inputPaddingLeft,
          '+',
          $theme.borders.inputBorderWidth
        ),
    fontSize: '14px',
    lineHeight: '18px',
    paddingTop: $collapseCaptionPadding ? 0 : '8px',
  })
);

const StyledInputRoot = withStyle(
  BaseStyledInputRoot,
  ({ $isFocused, $size, $theme }) => {
    let height = $theme.sizing.inputHeight;
    if ($size === 'compact') {
      height = $theme.sizing.inputCompactHeight;
    }
    if ($size === 'tiny') {
      height = $theme.sizing.inputTinyHeight;
    }
    const borderColors = $isFocused
      ? {
          borderTopColor: $theme.colors.primary,
          borderRightColor: $theme.colors.primary,
          borderBottomColor: $theme.colors.primary,
          borderLeftColor: $theme.colors.primary,
        }
      : {};
    return {
      ...borderColors,
      height,
    };
  }
);

export const StyledInput = withStyle(
  BaseStyledInput,
  ({
    $error,
    $isFocused,
    $positive,
    $theme,
    $inputStyles,
    label,
    size,
    type,
    value,
  }) => {
    let paddingTop = $theme.sizing.inputPaddingTop;
    if (!label) {
      paddingTop = '10px';
    }
    if (size === 'compact') {
      paddingTop = $theme.sizing.inputCompactPaddingTop;
      if (!label) {
        paddingTop = '6px';
      }
    }
    if (size === 'tiny') {
      paddingTop = $theme.sizing.inputTinyPaddingTop;
    }
    let placeHolderTop = '12px';
    if (size === 'compact') {
      placeHolderTop = '7px';
    }
    if (size === 'tiny') {
      placeHolderTop = '3px';
    }
    const additionalStyles = { ...$inputStyles };
    if (type === 'number') {
      const spinButtonStyles = {
        '-webkit-appearance': 'none',
        margin: 0,
      };
      additionalStyles['::-webkit-inner-spin-button'] = spinButtonStyles;
      additionalStyles['::-webkit-outer-spin-button'] = spinButtonStyles;
    }
    if (type === 'month') {
      additionalStyles['-webkit-appearance'] = 'none';
      additionalStyles['::-webkit-date-and-time-value'] = {
        textAlign: 'start',
      };
    }
    let focusLabelDisplay = 'block';
    let focusLabelTop = 0;
    if (size === 'compact') {
      focusLabelDisplay = 'none';
    }
    if (size === 'tiny') {
      if (value && value.length) {
        focusLabelDisplay = 'none';
      } else {
        focusLabelTop = placeHolderTop;
      }
    }
    return {
      backgroundColor: getInputFillColor({
        $theme,
        $error,
        $isFocused,
        $positive,
      }),
      paddingTop,
      paddingLeft: $theme.sizing.inputPaddingLeft,
      position: 'relative',
      ':empty+label': {
        display: 'block',
      },
      ':placeholder-shown+label': {
        top: placeHolderTop,
        fontSize: size === 'compact' ? '12px' : '15px',
        display: 'block',
      },
      ':not(:placeholder-shown)+label': {
        display: size === 'tiny' ? 'none' : 'block',
      },
      ':focus+label': {
        color: $theme.colors.grayLabel,
        display: focusLabelDisplay,
        fontSize: '15px',
        transition: '200ms all linear',
        top: focusLabelTop,
      },
      ...additionalStyles,
    };
  }
);

const StyledCompactInput = withStyle(StyledInput, ({ $theme }) => ({
  paddingLeft: $theme.sizing.inputCompactPadding,
  paddingRight: $theme.sizing.inputCompactPadding,
}));

const StyledTinyInput = withStyle(StyledInput, ({ $theme }) => ({
  fontSize: '16px',
  paddingLeft: $theme.sizing.inputTinyPadding,
  paddingRight: 0,
}));

export const getDefaultInputId = () =>
  `input-${Math.random().toString(36).slice(5)}`;

const TinyLabel = styled('div', ({ $theme }) => ({
  color: $theme.colors.grayLabel,
  fontSize: '12px',
  whiteSpace: 'nowrap',
}));

const LabeledInput = React.forwardRef(
  (
    {
      $value = '',
      caption = null,
      id,
      $disabled = false,
      $error = false,
      $isFocused = false,
      $positive = false,
      label = '',
      size = 'default',
      ...props
    },
    inputRef
  ) => {
    let StyledInputComponent = StyledInput;

    if (size === 'compact') {
      StyledInputComponent = StyledCompactInput;
    }
    if (size === 'tiny') {
      StyledInputComponent = StyledTinyInput;
    }
    return (
      <>
        <StyledInputComponent
          $error={$error}
          $isFocused={$isFocused}
          $positive={$positive}
          caption={caption}
          id={id}
          label={label}
          size={size}
          ref={inputRef}
          {...props}
        />
        <Label
          htmlFor={id}
          $disabled={$disabled}
          $error={$error}
          $isFocused={$isFocused}
          $positive={$positive}
          $size={size}
          $value={$value}
        >
          {label}
        </Label>
      </>
    );
  }
);
LabeledInput.propTypes = {
  // eslint-disable-next-line react/require-default-props
  $disabled: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  $error: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  $isFocused: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  $positive: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  $value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // eslint-disable-next-line react/require-default-props
  caption: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  endEnhancer: PropTypes.node,
  id: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  label: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  size: PropTypes.oneOf(['default', 'compact', 'tiny']),
};

export const Input = React.forwardRef(
  (
    {
      $isFocused = false,
      caption = null,
      collapseCaptionPadding = false,
      collapseCaptionHorizontalPadding = false,
      disabled = false,
      endEnhancer = null,
      error = false,
      instanceId = null,
      label = '',
      onBlur = () => {},
      positive = false,
      size = 'default',
      startEnhancer = null,
      style = {},
      tinyLabel = null,
      value = '',
      overrides = {},
      $inputStyles = {},
      $collapseRightBorder = false,
      $collapseLeftBorder = false,
      ...props
    },
    inputRef
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [focus, setFocus] = useState($isFocused);
    const [, theme] = useStyletron();
    const passwordMaskToggleIconSize = '25px';
    const hasLabel = label !== null && label.trim() !== '';
    let { type = 'text' } = props;
    let fallbackRef = null;
    if (!inputRef) {
      fallbackRef = createRef();
    }
    if (type === 'password' && showPassword) {
      type = 'text';
    }
    const rootStyles = {
      ...(style || {}),
      ...($collapseLeftBorder && {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      }),
      ...($collapseRightBorder && {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderRightWidth: 0,
      }),
    };
    const sharedProps = {
      $hasTinyLabel: Boolean(tinyLabel),
      $isFocused: focus,
      $error: error,
      $positive: positive,
      $size: size,
    };
    if (startEnhancer) {
      sharedProps.$adjoined = ADJOINED.left;
    }
    let endEnhancerProp = endEnhancer;
    if (
      size === 'tiny' &&
      (tinyLabel || label) &&
      !endEnhancer &&
      (value || value.length)
    ) {
      endEnhancerProp = (
        <TinyLabel
          onClick={() => {
            if (inputRef && inputRef.current) {
              inputRef.current.focus();
            }
            if (fallbackRef && fallbackRef.current) {
              fallbackRef.current.focus();
            }
          }}
        >
          {tinyLabel || label}
        </TinyLabel>
      );
    }

    return (
      <>
        <BaseInput
          startEnhancer={startEnhancer}
          endEnhancer={endEnhancerProp}
          onFocus={() => {
            if (!focus) {
              setFocus(true);
            }
          }}
          onBlur={(evt) => {
            if (focus && !$isFocused) {
              setFocus(false);
            }
            onBlur(evt);
          }}
          disabled={disabled}
          {...props}
          overrides={{
            Root: {
              component: StyledInputRoot,
              props: {
                ...sharedProps,
                style: rootStyles,
              },
            },
            StartEnhancer: StyledInputEnhancer,
            EndEnhancer: StyledInputEnhancer,
            InputContainer: StyledInputContainer,
            Input: {
              component: LabeledInput,
              props: {
                ...props,
                ...sharedProps,
                $value: value,
                label,
                $disabled: disabled,
                ref: inputRef || fallbackRef,
                placeholder: focus || !hasLabel ? props.placeholder : ' ',
                id: props.id ? props.id : getDefaultInputId(),
                type,
                size,
                value,
                $inputStyles,
                ...(instanceId && { 'data-instance-id': instanceId }),
              },
              style: {
                backgroundColor: disabled
                  ? theme.colors.inputFillDisabled
                  : getInputFillColor({
                      $theme: theme,
                      $error: error,
                      $isFocused: focus,
                      $positive: positive,
                    }),
                color: disabled
                  ? theme.colors.grayLabel
                  : $inputStyles.color || theme.colors.black,
                caretColor: theme.colors.black,
              },
            },
            MaskToggleButton: {
              // eslint-disable-next-line react/no-unstable-nested-components
              component: () => (
                <StyledMaskToggleButton
                  onClick={() => setShowPassword(!showPassword)}
                  type="button"
                  $error={error}
                  $isFocused={focus}
                  $positive={positive}
                  $theme={theme}
                >
                  {showPassword && (
                    <HideIcon size={passwordMaskToggleIconSize} />
                  )}
                  {!showPassword && (
                    <ViewIcon size={passwordMaskToggleIconSize} />
                  )}
                </StyledMaskToggleButton>
              ),
            },
            ...overrides,
          }}
        />
        {caption && (
          <Caption
            $error={error}
            $isFocused={focus}
            $positive={positive}
            $collapseCaptionPadding={collapseCaptionPadding}
            $collapseCaptionHorizontalPadding={collapseCaptionHorizontalPadding}
          >
            {caption}
          </Caption>
        )}
      </>
    );
  }
);
Input.propTypes = {
  // eslint-disable-next-line react/require-default-props
  $collapseLeftBorder: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  $collapseRightBorder: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  $inputStyles: PropTypes.object,
  // eslint-disable-next-line react/require-default-props
  $isFocused: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  caption: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  collapseCaptionHorizontalPadding: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  collapseCaptionPadding: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  disabled: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  endEnhancer: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]),
  // eslint-disable-next-line react/require-default-props
  error: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  id: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  instanceId: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  label: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  onBlur: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  overrides: PropTypes.object,
  // eslint-disable-next-line react/require-default-props
  placeholder: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  positive: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  size: PropTypes.oneOf(['default', 'compact', 'tiny']),
  // eslint-disable-next-line react/require-default-props
  startEnhancer: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]),
  // eslint-disable-next-line react/require-default-props
  style: PropTypes.object,
  // eslint-disable-next-line react/require-default-props
  tinyLabel: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  type: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const StatefulInput = React.forwardRef(
  (
    {
      $isFocused = false,
      caption = null,
      collapseCaptionPadding = false,
      endEnhancer = null,
      error = false,
      id = null,
      label = '',
      positive = false,
      type = 'text',
      ...props
    },
    inputRef
  ) => (
    <StatefulContainer
      $isFocused={$isFocused}
      collapseCaptionPadding={collapseCaptionPadding}
      endEnhancer={endEnhancer}
      error={error}
      id={id}
      label={label}
      positive={positive}
      type={type}
      {...props}
    >
      {(childrenProps) => (
        <Input caption={caption} {...childrenProps} ref={inputRef} />
      )}
    </StatefulContainer>
  )
);
StatefulInput.propTypes = {
  // eslint-disable-next-line react/require-default-props
  $collapseLeftBorder: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  $collapseRightBorder: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  $isFocused: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  caption: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  collapseCaptionPadding: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  endEnhancer: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  error: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  id: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  label: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  positive: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  type: PropTypes.string,
};
